import { useState, useCallback, useRef } from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useDiscardGuard } from '@/components/ui/form-dialog-content'
import { Button } from '@/components/ui/button'
import { MovimientoForm, type MovimientoFormValues } from './MovimientoForm'
import type { Movimiento, Cuenta } from '@/types/database'
import type { VistaSaldoClienteConImpuestos } from '@/services/clientesService'

interface MovimientoFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  movimiento?: Movimiento | null
  clientes: VistaSaldoClienteConImpuestos[]
  cuentas: Cuenta[]
  onSubmit: (data: MovimientoFormValues) => void
  isLoading?: boolean
  clienteIdFijo?: string
  tipoFijo?: 'Deuda' | 'Pago'
}

export function MovimientoFormDialog({
  open,
  onOpenChange,
  movimiento,
  clientes,
  cuentas,
  onSubmit,
  isLoading,
  clienteIdFijo,
  tipoFijo,
}: MovimientoFormDialogProps) {
  const [isFormDirty, setIsFormDirty] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  const isEditing = !!movimiento

  const clienteNombre = clienteIdFijo
    ? clientes.find((c) => c.id === clienteIdFijo)?.nombre
    : null

  // Determinar titulo segun el tipo fijo
  const getTitulo = () => {
    if (movimiento) return 'Editar Movimiento'
    if (tipoFijo === 'Deuda') return 'Registrar Deuda'
    if (tipoFijo === 'Pago') return 'Registrar Pago'
    return 'Nuevo Movimiento'
  }

  const getDescripcion = () => {
    if (movimiento) return 'Modifica los datos del movimiento'
    if (tipoFijo === 'Deuda') return 'Registra una deuda esporadica o trabajo puntual'
    if (tipoFijo === 'Pago') return 'Registra un pago del cliente'
    return 'Completa los datos para registrar un nuevo movimiento'
  }

  const getBotonTexto = () => {
    if (isLoading) return 'Guardando...'
    if (isEditing) return 'Actualizar'
    if (tipoFijo === 'Deuda') return 'Registrar Deuda'
    if (tipoFijo === 'Pago') return 'Registrar Pago'
    return 'Crear Movimiento'
  }

  const { holdClose, confirmation } = useDiscardGuard({
    isDirty: isFormDirty,
    onDiscard: () => onOpenChange(false),
  })

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && holdClose()) return
    onOpenChange(newOpen)
  }

  // Trigger submit desde el boton externo
  const handleExternalSubmit = useCallback(() => {
    formRef.current?.requestSubmit()
  }, [])

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col p-0">
          <SheetHeader className="px-6 pt-6">
            <SheetTitle>
              {getTitulo()}
              {clienteNombre && (
                <span className="text-muted-foreground font-normal">
                  {' '}
                  - {clienteNombre}
                </span>
              )}
            </SheetTitle>
            <SheetDescription>
              {getDescripcion()}
            </SheetDescription>
          </SheetHeader>

          {/* Contenido scrolleable */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <MovimientoForm
              ref={formRef}
              movimiento={movimiento}
              clientes={clientes}
              cuentas={cuentas}
              onSubmit={onSubmit}
              onDirtyChange={setIsFormDirty}
              isLoading={isLoading}
              clienteIdFijo={clienteIdFijo}
              tipoFijo={tipoFijo}
              hideButtons
            />
          </div>

          {/* Footer sticky con botones */}
          <SheetFooter className="px-6 py-4 border-t bg-background">
            <div className="flex justify-end gap-3 w-full">
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleExternalSubmit}
                disabled={isLoading}
              >
                {getBotonTexto()}
              </Button>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {confirmation}
    </>
  )
}
